import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import Button from '../ui/Button';
import HomeFeedCard, { feedItemSpanClass } from './HomeFeedCard';
import type { HomeFeedItem, HomeFeedResponse, HomeQuickAction } from './homeTypes';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'actions', label: 'Actions' },
  { id: 'events', label: 'Events' },
  { id: 'passport', label: 'Passport' },
  { id: 'builds', label: 'Builds' },
  { id: 'clubs', label: 'Clubs' },
  { id: 'shops', label: 'Shops' },
] as const;

interface HomeFeedPanelProps {
  onErrorChange?: (error: boolean) => void;
}

export const HomeFeedPanel: React.FC<HomeFeedPanelProps> = ({ onErrorChange }) => {
  const navigate = useNavigate();
  const [feed, setFeed] = useState<HomeFeedResponse | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [undoKey, setUndoKey] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { cursor?: string; append?: boolean }) => {
      if (opts?.append) setLoadingMore(true);
      else setLoading(true);
      setError(false);
      onErrorChange?.(false);
      try {
        const data = await apiService.getHomeFeed({
          types: filter === 'all' ? undefined : filter,
          cursor: opts?.cursor,
          limit: 30,
        });
        setFeed((prev) => {
          if (opts?.append && prev) {
            const mergedSections = [...prev.sections];
            for (const section of data.sections) {
              const existing = mergedSections.find((s) => s.id === section.id);
              if (existing) {
                existing.items = [...existing.items, ...section.items];
              } else {
                mergedSections.push(section);
              }
            }
            return { ...data, sections: mergedSections, summary: prev.summary, quick_actions: prev.quick_actions };
          }
          return data;
        });
        void apiService.trackEvent('home_feed_loaded', {
          metadata: { filter, item_count: data.item_count ?? 0 },
        });
      } catch {
        setError(true);
        onErrorChange?.(true);
        if (!opts?.append) setFeed(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, onErrorChange]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const dismiss = async (item: HomeFeedItem) => {
    try {
      await apiService.setHomeFeedState('dismiss', item.item_key);
      setUndoKey(item.item_key);
      setFeed((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections
            .map((s) => ({
              ...s,
              items: s.items.filter((i) => i.item_key !== item.item_key),
            }))
            .filter((s) => s.items.length > 0),
        };
      });
      void apiService.trackEvent('home_feed_item_dismissed', {
        metadata: { item_key: item.item_key, type: item.type },
      });
    } catch {
      // keep card
    }
  };

  const undoDismiss = async () => {
    if (!undoKey) return;
    try {
      await apiService.setHomeFeedState('undismiss', undoKey);
      setUndoKey(null);
      void load();
    } catch {
      // ignore
    }
  };

  const runQuickAction = (action: HomeQuickAction) => {
    void apiService.trackEvent('home_feed_quick_action', {
      metadata: { action_id: action.id },
    });
    const href = action.web_url;
    if (href) navigate(href);
  };

  const summary = feed?.summary;
  const sections = feed?.sections || [];
  const quick = feed?.quick_actions || [];
  const empty = !loading && sections.length === 0;

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-10 lg:items-start">
      <div className="min-w-0">
        <header className="mb-5 sm:mb-6">
          <p className="spec-label text-zinc-500">{summary?.date_label || ''}</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight mt-1">
            {summary?.greeting || 'Welcome back'}
          </h2>
          <p className="mt-2 text-sm sm:text-[15px] text-zinc-400 max-w-2xl leading-relaxed">
            {summary?.headline || 'Your Bilt activity will show up here.'}
          </p>
          {summary && summary.counts.action_required > 0 ? (
            <p className="mt-2 text-xs text-orange-300/90">
              {summary.counts.action_required} need
              {summary.counts.action_required === 1 ? 's' : ''} attention today
            </p>
          ) : null}
        </header>

        <div
          className="sticky top-0 z-10 -mx-1 px-1 py-2 mb-4 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/[0.04]"
          role="toolbar"
          aria-label="Feed filters"
        >
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setFilter(f.id);
                    void apiService.trackEvent('home_feed_filter_used', { metadata: { filter: f.id } });
                  }}
                  className={`shrink-0 min-h-[40px] px-3 rounded-md text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 ${
                    active
                      ? 'bg-white/10 text-white'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                  }`}
                  aria-pressed={active}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-white/[0.08] bg-[#0e0e0e] px-4 py-4 mb-4" role="alert">
            <p className="text-sm text-zinc-400">The feed could not load. Your garage is still available.</p>
            <Button type="button" variant="secondary" className="mt-3 min-h-[44px]" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : null}

        {undoKey ? (
          <div
            className="mb-3 flex items-center justify-between gap-3 rounded-md border border-white/[0.08] bg-zinc-900/80 px-3 py-2 text-sm text-zinc-300"
            role="status"
          >
            <span>Item dismissed.</span>
            <button
              type="button"
              className="min-h-[40px] text-orange-300 hover:text-orange-200 font-medium"
              onClick={() => void undoDismiss()}
            >
              Undo
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading feed">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-lg bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : null}

        {empty ? (
          <div
            className="rounded-lg border border-dashed border-white/10 px-5 py-10 text-center"
            data-testid="home-feed-empty"
          >
            <p className="font-display text-lg text-white">You&apos;re caught up</p>
            <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
              When something needs attention, an event, Passport encounter, or build step will land here. Until then,
              Discover still has builds and events worth exploring.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button type="button" className="min-h-[44px]" onClick={() => navigate('/events')}>
                Find events
              </Button>
              <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => navigate('/discover')}>
                Explore builds
              </Button>
            </div>
          </div>
        ) : null}

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.id} aria-labelledby={`feed-section-${section.id}`}>
              <h3
                id={`feed-section-${section.id}`}
                className="spec-label text-zinc-500 mb-3 sticky top-12 z-[5] bg-[#0a0a0a]/85 py-1 backdrop-blur-sm"
              >
                {section.title}
              </h3>
              <ul className="list-none p-0 m-0 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
                {section.items.map((item) => (
                  <li key={item.item_key} className={feedItemSpanClass(item.presentation)}>
                    <HomeFeedCard item={item} onDismiss={item.dismissible ? dismiss : undefined} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {feed?.next_cursor ? (
          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              disabled={loadingMore}
              onClick={() => {
                void apiService.trackEvent('home_feed_older_loaded', { metadata: { cursor: feed.next_cursor } });
                void load({ cursor: feed.next_cursor!, append: true });
              }}
            >
              {loadingMore ? 'Loading…' : 'Load earlier activity'}
            </Button>
          </div>
        ) : null}
      </div>

      <aside className="mt-8 lg:mt-0 lg:sticky lg:top-20 space-y-4" aria-label="Quick actions">
        <div className="rounded-lg border border-white/[0.06] bg-[#0e0e0e]/80 px-4 py-4">
          <h3 className="spec-label text-zinc-500 mb-3">Quick actions</h3>
          <ul className="space-y-1.5 list-none p-0 m-0">
            {quick.map((action) => (
              <li key={action.id}>
                <button
                  type="button"
                  onClick={() => runQuickAction(action)}
                  className={`w-full min-h-[44px] text-left px-3 rounded-md text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 ${
                    action.emphasis === 'primary'
                      ? 'bg-orange-500/15 text-orange-200 hover:bg-orange-500/25'
                      : 'text-zinc-300 hover:bg-white/[0.05]'
                  }`}
                >
                  {action.label}
                </button>
              </li>
            ))}
            {quick.length === 0 ? (
              <li className="text-sm text-zinc-500 px-1">Actions appear based on your activity.</li>
            ) : null}
          </ul>
        </div>
        {summary ? (
          <div className="rounded-lg border border-white/[0.06] bg-[#0e0e0e]/60 px-4 py-4 hidden lg:block">
            <h3 className="spec-label text-zinc-500 mb-2">Snapshot</h3>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-zinc-500">Events</dt>
                <dd className="stat-numeral text-xl text-white">{summary.counts.events}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Passport</dt>
                <dd className="stat-numeral text-xl text-white">{summary.counts.passport}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Builds</dt>
                <dd className="stat-numeral text-xl text-white">{summary.counts.builds}</dd>
              </div>
              <div>
                <dt className="text-zinc-500">Actions</dt>
                <dd className="stat-numeral text-xl text-white">{summary.counts.action_required}</dd>
              </div>
            </dl>
          </div>
        ) : null}
      </aside>
    </div>
  );
};

export default HomeFeedPanel;
